import { caseApiService, NoMergeSaveResponse } from './caseApiService';
import { debugError } from '../utils/debug';
import { CaseData } from '../types/case';
import { flushPendingLocalEditsAndWait } from './pendingLocalEditsRegistry';

/**
 * Bulletproof auto-save: a single dirty-bit + single in-flight promise.
 *
 * The Provider registers a snapshot provider; this service reads the latest
 * caseData and etag at execute time, never at queue time. There is no queue
 * of historical snapshots — every concurrent edit during an in-flight save
 * coalesces into exactly one follow-up save.
 *
 * Race conditions closed:
 * - Stale etag in queued saves (latest-at-execute).
 * - Concurrent fetches (single inFlight promise; no path can start a second).
 * - Out-of-order responses (no two responses can ever be outstanding).
 * - pendingSave surviving a caseId switch (register() resets state; gen guard
 *   discards in-flight callbacks if the registration changes mid-fetch).
 * - Pause/resume overwriting newer data (mechanism removed entirely).
 * - Transient failure abandonment (capped exponential-backoff retry).
 */

interface Snapshot {
    caseId: string;
    caseData: CaseData;
    etag: string;
}

interface ServiceCallbacks {
    onSuccess: (response: NoMergeSaveResponse) => void;
    onError: (error: any) => void;
}

const DEBOUNCE_DELAY_MS = 3000;
const RETRY_BASE_MS = 1000;
const RETRY_CAP_MS = 30_000;
const MAX_RETRY_ATTEMPTS = 5;

export class BackgroundSaveService {
    private static dirty = false;
    private static inFlight: Promise<void> | null = null;
    private static debounceTimer: ReturnType<typeof setTimeout> | null = null;
    private static retryTimer: ReturnType<typeof setTimeout> | null = null;
    private static retryAttempt = 0;
    private static registrationGen = 0;
    private static snapshotProvider: (() => Snapshot | null) | null = null;
    private static callbacks: ServiceCallbacks | null = null;
    private static subscribers = new Set<() => void>();
    // AbortController for the current in-flight fetch. Aborted on deregister so
    // the network request is cancelled when the Provider unmounts (R10).
    private static abortController: AbortController | null = null;
    // Manual Save mode (see docs/AUTOSAVE_DETAILS.md "Manual Save mode"). When true,
    // markDirty still tracks dirty state (so hasWorkPending()/the FAB keep working) but
    // never starts the debounce timer — saves only happen via an explicit flush()/forceSave().
    private static manualMode = false;

    /**
     * Toggle Manual Save mode. Pushed from Provider.tsx whenever the effective
     * ManualSaveToggle flag + DB-backed ManualSaveEnabled setting changes. Enabling it
     * cancels any pending debounce timer so a queued auto-fire can't slip through.
     */
    static setManualMode(enabled: boolean): void {
        this.manualMode = enabled;
        if (enabled && this.debounceTimer) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = null;
        }
    }

    /**
     * Register a snapshot provider and callbacks. Cancels any timers, resets
     * the dirty bit and retry counter, and bumps the registration generation
     * so any in-flight save's success handler will be discarded if it lands
     * after the registration changes (e.g., user switched cases).
     */
    static register(provider: () => Snapshot | null, callbacks: ServiceCallbacks): void {
        this.cancelTimers();
        this.dirty = false;
        this.retryAttempt = 0;
        this.snapshotProvider = provider;
        this.callbacks = callbacks;
        this.registrationGen++;
        this.notify();
    }

    /**
     * Tear down. The in-flight fetch (if any) is allowed to resolve, but its
     * callbacks no-op because the gen will have advanced.
     */
    static deregister(): void {
        this.cancelTimers();
        this.dirty = false;
        this.retryAttempt = 0;
        this.snapshotProvider = null;
        this.callbacks = null;
        this.registrationGen++;
        // Cancel the in-flight network request so the browser doesn't hold the
        // connection open after the Provider unmounts (R10). The gen guard already
        // ensures callbacks no-op; this is purely a network cleanup.
        this.abortController?.abort();
        this.abortController = null;
        this.notify();
    }

    /**
     * Wipe pending state without changing the registered provider/callbacks.
     * Use when the underlying case has been replaced (e.g., loadCase fetched a new case)
     * — bumps the generation so any in-flight save's onSuccess will no-op (which would
     * otherwise stamp the new case's etagRef with the old case's etag).
     */
    static reset(): void {
        this.cancelTimers();
        this.dirty = false;
        this.retryAttempt = 0;
        this.registrationGen++;
        this.notify();
    }

    /**
     * Mark client state as dirty. By default kicks the debounce timer; pass
     * { startDebounce: false } for text-input keystrokes (the blur handler
     * is responsible for kicking the timer in that case so we don't fire
     * mid-typing).
     */
    static markDirty(opts?: { startDebounce?: boolean }): void {
        if (!this.snapshotProvider) {
            return;
        }
        // In Manual Save mode, dirty-tracking still runs (so hasWorkPending()/the Save FAB
        // reflect reality), but the debounce timer never starts — only an explicit
        // flush()/forceSave() persists. See docs/AUTOSAVE_DETAILS.md "Manual Save mode".
        const startDebounce = !this.manualMode && (opts?.startDebounce !== false);
        const wasDirty = this.dirty;
        this.dirty = true;
        if (startDebounce) {
            if (this.debounceTimer) clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(() => {
                this.debounceTimer = null;
                void this.runSave();
            }, DEBOUNCE_DELAY_MS);
        }
        if (!wasDirty) this.notify();
    }

    /**
     * Drain: first drain any page-local pending-edit flushers (see
     * pendingLocalEditsRegistry.ts — e.g. BudgetPage's/NetWorthPage's own short debounces,
     * which hold in-progress edits that haven't reached context/this service's dirty bit
     * yet), then cancel any pending debounce/retry, await any in-flight save, then if still
     * dirty fire one more save and await it. Resolves only when the server has the latest
     * snapshot. Used by forceSave, navigation guard, triggerMerge, and the pre-download save.
     */
    static async flush(): Promise<void> {
        await flushPendingLocalEditsAndWait();
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = null;
        }
        if (this.retryTimer) {
            clearTimeout(this.retryTimer);
            this.retryTimer = null;
        }
        if (this.inFlight) {
            try { await this.inFlight; } catch { /* swallow; surfaced via onError */ }
        }
        // Loop because a save might dirty itself again on transient retry.
        // Guard with attempts to avoid infinite loops if something is broken.
        let safety = 0;
        while (this.dirty && safety < 16) {
            safety++;
            const next = this.runSave();
            if (next) {
                try { await next; } catch { /* swallow; surfaced via onError */ }
            } else {
                break;
            }
        }
    }

    /**
     * Fire a synchronous keepalive save suitable for page-unload contexts (R6).
     *
     * Uses `fetch` with `keepalive: true` so the browser dispatches the request
     * even after the page begins to unload (hard tab close, OS-level kill).
     * Falls back to `navigator.sendBeacon` if keepalive fetch is unavailable.
     *
     * Call this from a `pagehide` or `beforeunload` handler where async operations
     * are not guaranteed to complete. Pass the full API base URL so the service
     * doesn't need to import the environment config.
     *
     * `apiBaseUrl` already ends in `/api` (see config.apiBaseUrl, e.g.
     * `https://dev.localhost:7201/api`), so the path appended here must not repeat it. It did,
     * and every beacon 404'd for as long as this has existed. Nothing surfaced it: the fetch
     * swallows its own errors by design, because there is no way to report a failure from a page
     * that is already unloading.
     */
    static flushBeacon(apiBaseUrl: string): void {
        if (!this.dirty && !this.inFlight && !this.debounceTimer && !this.retryTimer) return;
        const snapshot = this.snapshotProvider?.();
        if (!snapshot?.caseId || !snapshot.caseData) return;

        const url = `${apiBaseUrl}/v1/cases/save-no-merge`;
        const body = JSON.stringify(snapshot.caseData);

        try {
            // keepalive fetch is the preferred approach: it supports JSON content-type
            // and credentials, and fires even after the page starts unloading.
            //
            // If-Match matters more here than on the normal save path, not less. This tab may
            // have been sitting on a stale snapshot for a long time while another tab (or another
            // device) moved the case on. Without the header the server treats the write as an
            // unconditional overwrite, so simply closing the stale tab destroys the newer work --
            // silently, since a 412 is unreportable from a page that is already unloading.
            // Sending it means the server rejects the stale write and the newer version stands.
            fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(snapshot.etag ? { 'If-Match': snapshot.etag } : {}),
                },
                body,
                credentials: 'include',
                keepalive: true,
            }).catch(() => { /* best-effort — can't handle errors after unload */ });
        } catch {
            // Fallback: sendBeacon. Sends as text/plain (CORS-safelisted), relying
            // on the HTTP-only cookie for auth. The server must accept text/plain on
            // this route — the body is still valid JSON.
            //
            // Known limitation: sendBeacon cannot set request headers, so this path cannot carry
            // If-Match and remains an unconditional overwrite. It is only reached when keepalive
            // fetch is unavailable entirely, which is rare on supported browsers — but on that
            // path a stale tab can still clobber newer work. Closing it properly means moving the
            // concurrency token into the body and having the server read it from there.
            try {
                navigator.sendBeacon?.(url, body);
            } catch { /* truly best-effort */ }
        }
    }

    /** True if the client has any unpersisted intent (dirty, in-flight, or retry-scheduled). */
    static hasWorkPending(): boolean {
        return this.dirty || this.inFlight !== null || this.retryTimer !== null;
    }

    /** True only while a fetch is actually outstanding. */
    static isSaving(): boolean {
        return this.inFlight !== null;
    }

    /**
     * Subscribe to state-change notifications. The callback is invoked whenever
     * dirty / inFlight / retryTimer changes. Returns an unsubscribe function.
     */
    static subscribe(fn: () => void): () => void {
        this.subscribers.add(fn);
        return () => { this.subscribers.delete(fn); };
    }

    private static notify(): void {
        this.subscribers.forEach((fn) => {
            try { fn(); } catch (e) { debugError('BackgroundSaveService: subscriber threw', e); }
        });
    }

    private static cancelTimers(): void {
        if (this.debounceTimer) { clearTimeout(this.debounceTimer); this.debounceTimer = null; }
        if (this.retryTimer) { clearTimeout(this.retryTimer); this.retryTimer = null; }
    }

    /**
     * Internal: read the snapshot, fire one save, and on completion fire
     * exactly one follow-up if more edits arrived during the fetch.
     * Returns the in-flight promise (or null if nothing to save).
     */
    private static runSave(): Promise<void> | null {
        if (this.inFlight) return this.inFlight;
        if (!this.snapshotProvider || !this.callbacks) return null;
        if (!this.dirty) return null;

        // We're firing now — supersede any pending debounce timer so a stale tick
        // can't fire a redundant save after this one completes.
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = null;
        }

        const snapshot = this.snapshotProvider();
        if (!snapshot || !snapshot.caseId) return null;

        // Capture gen so a late-resolving response from a stale registration
        // doesn't apply to the new case.
        const myGen = this.registrationGen;

        // Clear dirty BEFORE fetch — concurrent edits during the fetch will
        // re-set it, guaranteeing exactly one coalesced follow-up save.
        this.dirty = false;
        this.notify();

        const promise = this.doSave(snapshot, myGen);
        this.inFlight = promise;
        this.notify();
        return promise;
    }

    private static async doSave(snapshot: Snapshot, gen: number): Promise<void> {
        const rawJson = JSON.stringify(snapshot.caseData);
        // Create a fresh AbortController for this request so deregister() can cancel it.
        const ctrl = new AbortController();
        this.abortController = ctrl;
        try {
            // Race the real fetch against an abort signal. This avoids threading the
            // signal through all of BaseApiService without changing the API surface.
            const abortPromise = new Promise<never>((_, reject) =>
                ctrl.signal.addEventListener('abort', () =>
                    reject(new DOMException('Save aborted', 'AbortError'))
                )
            );
            const response = await Promise.race([
                caseApiService.saveCaseNoMerge(snapshot.caseId, snapshot.caseData, snapshot.etag || ''),
                abortPromise,
            ]);
            if (gen === this.registrationGen && this.callbacks) {
                this.callbacks.onSuccess(response);
            }
            this.retryAttempt = 0;
        } catch (error) {
            // AbortError on deregister: discard silently — the gen guard already no-ops
            // callbacks; we just want the network cleanup, not an error toast.
            if ((error as DOMException)?.name === 'AbortError') {
                return;
            }

            // Preserve the existing backup-on-failure behavior so no save body is ever lost.
            await this.backupQuietly(snapshot.caseId, rawJson, error);

            if (this.isTransient(error) && this.retryAttempt < MAX_RETRY_ATTEMPTS) {
                // Mark dirty so the retry timer's runSave will fire. We don't
                // call onError for transient retries — the user will only see
                // the toast if we exhaust attempts.
                this.dirty = true;
                this.scheduleRetry();
            } else {
                this.retryAttempt = 0;
                if (gen === this.registrationGen && this.callbacks) {
                    this.callbacks.onError(error);
                }
            }
        } finally {
            if (this.abortController === ctrl) {
                this.abortController = null;
            }
            this.inFlight = null;
            this.notify();
            // Coalesce: any edits made during the fetch left dirty=true.
            // Fire exactly one follow-up save on the next tick (no debounce).
            if (this.dirty && !this.retryTimer && this.snapshotProvider) {
                setTimeout(() => { void this.runSave(); }, 0);
            }
        }
    }

    private static scheduleRetry(): void {
        if (this.retryTimer) clearTimeout(this.retryTimer);
        const delayMs = Math.min(RETRY_BASE_MS * Math.pow(2, this.retryAttempt), RETRY_CAP_MS);
        this.retryAttempt++;
        this.retryTimer = setTimeout(() => {
            this.retryTimer = null;
            this.notify();
            void this.runSave();
        }, delayMs);
    }

    private static isTransient(error: any): boolean {
        const status = error?.status;
        if (status === undefined || status === null) return true; // network / abort / other
        if (status >= 500) return true;
        if (status === 429) return true;
        if (error instanceof TypeError) return true; // fetch network error
        return false;
    }

    private static async backupQuietly(caseId: string, rawJson: string, error: any): Promise<void> {
        try {
            const errorMessage = error instanceof Error ? error.message : String(error);
            const errorType = error instanceof Error ? error.constructor.name : 'UnknownError';
            await caseApiService.backupCase(caseId, rawJson, errorMessage, errorType);
        } catch (backupError) {
            debugError('BackgroundSaveService: backup failed', backupError);
        }
    }
}
